/**
 * Authentication utilities for JWT token management
 */

const TOKEN_STORAGE_KEY = "auth_token";

/**
 * Decode JWT token without verification (client-side only)
 * Note: Real verification happens on the server
 */
function decodeToken(
  token: string
): { exp?: number; [key: string]: unknown } | null {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Check if token is expired
 */
function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  const exp = decoded.exp * 1000; // Convert to milliseconds
  return Date.now() >= exp;
}

/**
 * Get stored token from localStorage
 */
export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) return null;
  if (isTokenExpired(token)) {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    return null;
  }
  return token;
}

/**
 * Store token in localStorage and cookie (for server-side access)
 */
export function setToken(token: string): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  // Also set in cookie for server-side API route access
  // Use SameSite=Lax for same-site requests (cookies are sent automatically for same-origin)
  // JWT tokens are URL-safe, so no encoding needed
  const isSecure = window.location.protocol === "https:";
  document.cookie = `auth_token=${token}; path=/; max-age=${
    60 * 60
  }; SameSite=Lax${isSecure ? "; Secure" : ""}`;
}

/**
 * Remove token from localStorage and cookie
 */
export function removeToken(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  // Remove cookie
  document.cookie = "auth_token=; path=/; max-age=0";
}

/**
 * Check if user is authenticated (has valid token)
 */
export function isAuthenticated(): boolean {
  const token = getToken();
  if (!token) {
    return false;
  }

  // Double-check expiration
  if (isTokenExpired(token)) {
    removeToken();
    return false;
  }

  return true;
}

/**
 * Login function - calls backend login endpoint
 */
export async function login(password: string): Promise<string> {
  const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL ?? "/api";
  const loginUrl = `${BACKEND_URL}/login`;

  const response = await fetch(loginUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ password }),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: "Login failed" }));
    throw new Error(error.detail || "Login failed");
  }

  const data = await response.json();
  if (!data.access_token) {
    throw new Error("No token received");
  }

  setToken(data.access_token);
  return data.access_token;
}

/**
 * Logout function
 */
export function logout(): void {
  removeToken();
}
